import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Boxes, TriangleAlert, Ship, Inbox } from "lucide-react";
import { api, gbp, num, dateTime } from "@/lib/api";
import { AvailabilityBlock } from "@/components/Availability";
import { AppShell, PageHeader, Panel, SkeletonRows, EmptyState, Tag } from "@/components/Shell";

const KPI = ({ label, value, sub, tone = "slate", testId, to }) => {
  const body = (
    <div className="border border-slate-200 bg-white p-4 transition-colors hover:border-slate-400" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className={`num mt-1 text-2xl font-medium ${tone === "red" ? "text-red-600" : "text-slate-900"}`}>{value}</p>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
};

const TYPE_TONE = {
  commit: "amber", sale: "blue", receipt: "green", opening_stock: "slate",
  po_ordered: "violet", void_reversal: "red", credit_restock: "green",
};

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  return (
    <AppShell>
      <PageHeader
        title="Dashboard"
        subtitle="Everything at a glance: the four availability numbers, live movements and open exceptions."
        testId="dashboard-header"
      />

      {!data ? (
        <div className="border border-slate-200 bg-white"><SkeletonRows rows={5} /></div>
      ) : (
        <div className="space-y-4" data-testid="dashboard-content">
          <Panel title="Total availability across all locations" testId="dashboard-availability">
            <AvailabilityBlock data={data.totals} testIdPrefix="dash-avail" />
          </Panel>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <KPI label="Stock value" value={gbp(data.stock_value_pence)} sub="Moving-average cost, GBP" testId="kpi-stock-value" />
            <KPI label="SKUs" value={num(data.sku_count)} sub="Active products" testId="kpi-sku-count" to="/app/products" />
            <KPI label="Gaps" value={num(data.gap_count)} tone={data.gap_count ? "red" : "slate"} sub="Shortfalls flagged" testId="kpi-gap-count" to="/app/gaps" />
            <KPI label="Open POs" value={num(data.open_pos)} sub="Ordered / on the water" testId="kpi-open-pos" to="/app/purchasing" />
            <KPI label="Unmatched lines" value={num(data.unmatched_count)} tone={data.unmatched_count ? "red" : "slate"} sub="Invoice lines to map" testId="kpi-unmatched" to="/app/invoices" />
          </div>

          <Panel
            title="Live stock movements"
            testId="dashboard-movements"
            right={<Link to="/app/reports" className="text-xs text-slate-500 hover:text-slate-900">Full movement report</Link>}
          >
            {data.recent_movements?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                    <tr>
                      <th className="px-4 py-2 text-left">SKU</th>
                      <th className="px-4 py-2 text-left">Event</th>
                      <th className="px-4 py-2 text-right">On hand Δ</th>
                      <th className="px-4 py-2 text-right">Committed Δ</th>
                      <th className="px-4 py-2 text-left">Reference</th>
                      <th className="px-4 py-2 text-left">When</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.recent_movements.map((m, i) => (
                      <tr key={i} className="hover:bg-slate-50" data-testid={`movement-row-${i}`}>
                        <td className="mono px-4 py-2">{m.sku}</td>
                        <td className="px-4 py-2">
                          <Tag tone={TYPE_TONE[m.type] || "slate"}>{m.type.replace(/[:_]/g, " ")}</Tag>
                        </td>
                        <td className="num px-4 py-2 text-right">{m.qty_on_hand > 0 ? `+${m.qty_on_hand}` : m.qty_on_hand || "—"}</td>
                        <td className="num px-4 py-2 text-right">{m.qty_committed > 0 ? `+${m.qty_committed}` : m.qty_committed || "—"}</td>
                        <td className="mono px-4 py-2 text-xs text-slate-500">{m.reference || "—"}</td>
                        <td className="px-4 py-2 text-xs text-slate-500">{dateTime(m.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState
                icon={Inbox}
                title="No movements yet"
                body="Upload opening stock or simulate a Xero invoice to see stock move in real time."
                testId="movements-empty"
                action={
                  <Link to="/app/invoices" className="bg-slate-900 px-3 py-2 text-sm text-white" data-testid="movements-empty-cta">
                    Simulate an invoice
                  </Link>
                }
              />
            )}
          </Panel>

          <div className="grid gap-4 md:grid-cols-3">
            <Link to="/app/products" className="border border-slate-200 bg-white p-4 transition-colors hover:border-slate-900" data-testid="quick-inventory">
              <Boxes className="h-5 w-5 text-slate-400" />
              <p className="mt-3 font-display text-sm font-semibold">Inventory</p>
              <p className="text-xs text-slate-500">Search 40k+ SKUs with server-side paging.</p>
            </Link>
            <Link to="/app/gaps" className="border border-slate-200 bg-white p-4 transition-colors hover:border-slate-900" data-testid="quick-gaps">
              <TriangleAlert className="h-5 w-5 text-slate-400" />
              <p className="mt-3 font-display text-sm font-semibold">Gap engine</p>
              <p className="text-xs text-slate-500">Shortfalls, causes and substitute cover.</p>
            </Link>
            <Link to="/app/purchasing" className="border border-slate-200 bg-white p-4 transition-colors hover:border-slate-900" data-testid="quick-purchasing">
              <Ship className="h-5 w-5 text-slate-400" />
              <p className="mt-3 font-display text-sm font-semibold">Incoming</p>
              <p className="text-xs text-slate-500">Track containers on the water with ETAs.</p>
            </Link>
          </div>
        </div>
      )}
    </AppShell>
  );
}
